import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaEnvelope, FaExclamationCircle, FaCheckCircle, FaArrowLeft
} from 'react-icons/fa';
import { getApiEndpoint } from '../../../../config/api';
import allSafeLogo from '../../../../assets/cloudsafe-logo.png';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [serverError, setServerError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [touched, setTouched] = useState(false);

  const validateEmail = (value) => {
    if (!value.trim()) return 'Email address is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
    return '';
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (fieldError) setFieldError('');
    if (serverError) setServerError('');
  };

  const handleBlur = () => {
    setTouched(true);
    setFieldError(validateEmail(email));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    const error = validateEmail(email);
    setFieldError(error);
    setTouched(true);
    if (error) return;

    setIsLoading(true);

    try {
      const response = await fetch(getApiEndpoint('/api/auth/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });

      if (!response.ok) {
        let data;
        try {
          data = await response.json();
        } catch (_) {
          throw new Error('Failed to send reset email. Please try again.');
        }
        throw new Error(data.message || 'Failed to send reset email');
      }

      setResetSent(true);
    } catch (error) {
      setServerError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const hasError = touched && fieldError;

  return (
    <div className="fp-page">
      <header className="fp-header">
        <img src={allSafeLogo} alt="CloudSafe" className="fp-logo" />
        <div className="fp-header-right">
          <span className="fp-header-text">Remember your password?</span>
          <Link to="/login" className="fp-header-link">Sign in</Link>
        </div>
      </header>

      <main className="fp-body">
        <div className="fp-card">
          {!resetSent ? (
            <>
              <h1 className="fp-title">Reset password</h1>
              <p className="fp-subtitle">
                Enter your email address and we'll send you instructions to reset your password.
              </p>

              {serverError && (
                <div className="fp-error-alert" role="alert">
                  <FaExclamationCircle className="fp-error-alert-icon" />
                  <span>{serverError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="fp-form" noValidate>
                <div className="fp-field">
                  <label className="fp-label" htmlFor="fp-email">Email address</label>
                  <div className="fp-input-wrap">
                    <FaEnvelope className="fp-input-icon" />
                    <input
                      id="fp-email"
                      type="email"
                      className={`fp-input${hasError ? ' has-error' : ''}`}
                      placeholder="Enter your email"
                      value={email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="email"
                      inputMode="email"
                      aria-describedby={hasError ? 'fp-email-error' : undefined}
                      required
                    />
                  </div>
                  {hasError && (
                    <p className="fp-field-error" id="fp-email-error" role="alert">
                      {fieldError}
                    </p>
                  )}
                </div>

                <button type="submit" className="fp-submit" disabled={isLoading}>
                  {isLoading && <span className="fp-spinner" />}
                  {isLoading ? 'Sending...' : 'Send reset instructions'}
                </button>
              </form>

              <Link to="/login" className="fp-back-link">
                <FaArrowLeft /> Back to sign in
              </Link>
            </>
          ) : (
            <div className="fp-success">
              <div className="fp-success-icon-wrap">
                <FaCheckCircle className="fp-success-icon" />
              </div>
              <h2 className="fp-success-title">Check your email</h2>
              <p className="fp-success-text">
                We've sent password reset instructions to your email address.
                Please check your inbox and follow the link to reset your password.
              </p>
              <Link to="/login" className="fp-success-btn">
                Return to sign in
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
